import {routerReducer, RouterReducerState} from '@ngrx/router-store';
import {ActionReducerMap, createSelector, MemoizedSelector} from '@ngrx/store';
import * as _ from 'lodash';
import {Auth0UserInfo, Client, Culture, Office, Role, Timezone, User} from '..';
import {Site} from '../models/site';
import {SubscriptionI} from '../models/subscription-i';
import {RouterStateUrl} from '../models/router-state-url';
import {culturesReducer, culturesSelectors, CulturesState} from './cultures';
import {externalLinksReducer, externalLinksSelectors, ExternalLinksState} from './external-links';
import {impersonableUsersReducer, impersonableUsersSelectors, ImpersonableUsersState} from './impersonable-users';
import {layoutReducer, layoutSelectors, LayoutState} from './layout';
import {myClientsReducer, myClientsSelectors, MyClientsState} from './my-clients';
import {timezonesReducer, timezonesSelectors, TimezonesState} from './timezones';
import {userContextReducer, userContextSelectors, UserContextState} from './user-context';
import {userInfoReducer, userInfoSelectors, UserInfoState} from './user-info';
import {mySitesReducer, mySitesSelectors, MySitesState} from './my-sites';
import {mySubscriptionsReducer, mySubscriptionsSelectors, MySubscriptionsState} from './my-subscriptions';
import {countriesReducer, countriesSelectors, CountriesState} from './countries';
import {Country} from '../models/country';
import {GroupRole} from '../models/groupe-role';
import {groupRolesReducer, groupRolesSelectors, GroupRolesState} from './groupe-roles';
import {Preference} from '../models/preferences';
import {MemoizedSelectorWithProps} from '@ngrx/store/src/selector';
import {UsersReducer, usersSelectors, UsersState} from './users';
import {myOrdersReducer, myOrdersSelectors, MyOrdersState} from './my-orders';
import {Order, SelectedItem} from '../../order/models/order';
import {testDataReducer, testDataSelectors, TestDataState} from './test-data';
import {productsReducer, productsSelectors, ProductsState} from './products';
import {ServerReducer, serverSelectors, ServerState} from './servers';
import {BillingProduct, Product} from '../models/product';
import {ServerForm, ServerFormUrl} from '../models/servers';
import {IBcData} from '../models/bread-crumb-data';
import {DeviceAvailableReducer, deviceAvailableSelectors, DeviceAvailableState} from './device-available';
import {Device} from '../models/device';
import {TransportersReducer, transportersSelectors, TransportersState} from './transporters';
import {Transporter} from '../models/transporter';
import {playerConfigurationEditReducer, playerConfigurationEditSelectors, PlayerConfigurationEditState} from './player-configuration-edit';
import {PlayerConfigurationEdit} from '../models/player-configuration';
import {
  playerConfigurationDefinitionReducer,
  playerConfigurationDefinitionSelectors,
  PlayerConfigurationDefinitionState
} from './player-configuration-definition';
import {PlayerConfigurationDefinition} from '../models/player-config-list-option';
import {productsCompleteListReducer, productsCompleteListSelectors, ProductsCompleteListState} from './product-complete-list';
import { quickSearchSelectors, QuickSearchState, quickSearchReducer } from './quick-search';
import {companiesLinkContractReducer, companiesLinkContractSelectors, CompaniesLinkContractState} from './companies-link-contact';
import {subscriptionsLinkContractReducer, subscriptionsLinkContractSelectors, SubscriptionsLinkContractState} from './subscription-link-contract';
import { ContractLinkReducer, contractLinkSelectors, ContractLinkState} from './contract-link';
import {invoicesListSelectors, InvoicesState, InvoicesStateListReducer} from './invoices-list';
import {ContractFilter} from '../../contract/models/contract-filter';
import {headerTitleReducer, headerTitleSelectors, HeaderTitleState} from './header-title';
import {supportUsersReducer, supportUsersSelectors, SupportUsersState} from './support-users';
import {SupportUsers} from '../models/support-users';
import {productionLineReducer, productionLineSelectors, ProductionLineState} from './production-line';
import { officesReducer, officesSelectors, OfficesState} from './offices';
import {SubListReducer, subListSelectors, SubListState} from './subscription-list';
import {SubscriptionsFilter, SubscriptionsList} from '../models/subscription';
import {ServerUrlReducer, serverUrlSelectors, ServerUrlState} from './servers-url';
import {clientConfigurationReducer, clientConfigurationSelectors, ClientConfigurationState} from './client-configuration';
import {cmsReducer, cmsSelectors, CmsState} from './cms';
import {billingProductsReducer, billingProductsSelectors, BillingProductsState} from './billing-products';
import {servicesReducer, servicesSelectors, ServicesState} from './services';
import {Service} from '../models/service';
import {ConceptsReducer, conceptsSelectors, ConceptsState} from './concepts';
import {Concept} from '../models/concept';
import {BillingLicencesReducer, billingLicencesSelectors, BillingLicencesState} from './licences';

/* tslint:disable */
export interface AppState {
  testData: TestDataState;
  countries: CountriesState;
  cultures: CulturesState;
  offices: OfficesState;
  productionLine: ProductionLineState;
  supportUsers: SupportUsersState;
  externalLinks: ExternalLinksState;
  groupRoles: GroupRolesState;
  impersonableUsers: ImpersonableUsersState;
  layout: LayoutState;
  myClients: MyClientsState;
  myOrders: MyOrdersState;
  mySites: MySitesState;
  mySubscriptions: MySubscriptionsState;
  products: ProductsState;
  services: ServicesState;
  productsCompleteList: ProductsCompleteListState;
  router: RouterReducerState<RouterStateUrl>;
  timezones: TimezonesState;
  userInfo: UserInfoState;
  userContext: UserContextState;
  users: UsersState;
  server: ServerState;
  serverUrl: ServerUrlState;
  deviceAvailable: DeviceAvailableState;
  transporters: TransportersState;
  playerConfigurationEdit: PlayerConfigurationEditState;
  playerConfigDef: PlayerConfigurationDefinitionState;
  quickSearch: QuickSearchState;
  companiesLinkContract: CompaniesLinkContractState;
  subscriptionsLinkContract: SubscriptionsLinkContractState;
  contractLink: ContractLinkState;
  invoicesList: InvoicesState,
  headerTitle: HeaderTitleState,
  subscriptionList: SubListState;
  clientConfiguration: ClientConfigurationState,
  cms: CmsState,
  billingProducts: BillingProductsState;
  concepts: ConceptsState;
  licences: BillingLicencesState;
}

export const reducers: ActionReducerMap<AppState> = {
  testData: testDataReducer,
  countries: countriesReducer,
  cultures: culturesReducer,
  offices: officesReducer,
  productionLine: productionLineReducer,
  supportUsers: supportUsersReducer,
  externalLinks: externalLinksReducer,
  groupRoles: groupRolesReducer,
  impersonableUsers: impersonableUsersReducer,
  layout: layoutReducer,
  myClients: myClientsReducer,
  myOrders: myOrdersReducer,
  mySites: mySitesReducer,
  mySubscriptions: mySubscriptionsReducer,
  products: productsReducer,
  services: servicesReducer,
  productsCompleteList: productsCompleteListReducer,
  router: routerReducer,
  timezones: timezonesReducer,
  userInfo: userInfoReducer,
  userContext: userContextReducer,
  users: UsersReducer,
  server: ServerReducer,
  serverUrl: ServerUrlReducer,
  deviceAvailable: DeviceAvailableReducer,
  transporters: TransportersReducer,
  playerConfigurationEdit: playerConfigurationEditReducer,
  playerConfigDef: playerConfigurationDefinitionReducer,
  quickSearch: quickSearchReducer,
  companiesLinkContract: companiesLinkContractReducer,
  subscriptionsLinkContract: subscriptionsLinkContractReducer,
  contractLink: ContractLinkReducer,
  invoicesList: InvoicesStateListReducer,
  headerTitle: headerTitleReducer,
  subscriptionList: SubListReducer,
  clientConfiguration: clientConfigurationReducer,
  cms: cmsReducer,
  billingProducts: billingProductsReducer,
  concepts: ConceptsReducer,
  licences: BillingLicencesReducer,

};

const rootSelectors = {
  testData: (state: AppState) => _.get(state, 'testData'),
  countries: (state: AppState) => _.get(state, 'countries'),
  cultures: (state: AppState) => _.get(state, 'cultures'),
  offices: (state: AppState) => _.get(state, 'offices'),
  productionLine: (state: AppState) => _.get(state, 'productionLine'),
  supportUsers: (state: AppState) => _.get(state, 'supportUsers'),
  externalLinks: (state: AppState) => _.get(state, 'externalLinks'),
  groupRoles: (state: AppState) => _.get(state, 'groupRoles'),
  impersonableUsers: (state: AppState) => _.get(state, 'impersonableUsers'),
  layout: (state: AppState) => _.get(state, 'layout'),
  myClients: (state: AppState) => _.get(state, 'myClients'),
  myOrders: (state: AppState) => _.get(state, 'myOrders'),
  mySites: (state: AppState) => _.get(state, 'mySites'),
  mySubscriptions: (state: AppState) => _.get(state, 'mySubscriptions'),
  router: (state: any) => _.get(state, 'router'),
  timezones: (state: AppState) => _.get(state, 'timezones'),
  userInfo: (state: AppState) => _.get(state, 'userInfo'),
  userContext: (state: AppState) => _.get(state, 'userContext'),
  users: (state: AppState) => _.get(state, 'users'),
  products: (state: AppState) => _.get(state, 'products'),
  services: (state: AppState) => _.get(state, 'services'),
  productsCompleteList: (state: AppState) => _.get(state, 'productsCompleteList'),
  server: (state: AppState) => _.get(state, 'server'),
  serverUrl: (state: AppState) => _.get(state, 'serverUrl'),
  deviceAvailable: (state: AppState) => _.get(state, 'deviceAvailable'),
  transporters: (state: AppState) => _.get(state, 'transporters'),
  playerConfigurationEdit: (state: AppState) => _.get(state, 'playerConfigurationEdit'),
  playerConfigDef: (state: AppState) => _.get(state, 'playerConfigDef'),
  quickSearch: (state: AppState) => _.get(state, 'quickSearch'),
  companiesLinkContract: (state: AppState) => _.get(state, 'companiesLinkContract'),
  subscriptionsLinkContract: (state: AppState) => _.get(state, 'subscriptionsLinkContract'),
  contractLink: (state: AppState) => _.get(state, 'contractLink'),
  invoicesList: (state: AppState) => _.get(state, 'invoicesList'),
  headerTitle: (state: AppState) => _.get(state, 'headerTitle'),
  subscriptionList: (state: AppState) => _.get(state, 'subscriptionList'),
  clientConfiguration: (state: AppState) => _.get(state, 'clientConfiguration'),
  cms: (state: AppState) => _.get(state, 'cms'),
  billingProducts: (state: AppState) => _.get(state, 'billingProducts'),
  concepts: (state: AppState) => _.get(state, 'concepts'),
  licences: (state: AppState) => _.get(state, 'licences'),
};
export interface Selectors {
  getTestData: MemoizedSelector<AppState, number[]>;
  getCountries: MemoizedSelector<AppState, Country[]>;
  getCultures: MemoizedSelector<AppState, Culture[]>;
  getOffices: MemoizedSelector<AppState, Office[]>;
  getProductionLine: MemoizedSelector<AppState, any[]>;
  getSupportUsers: MemoizedSelector<AppState, SupportUsers[]>;
  getCurrentClient: MemoizedSelector<AppState, Client>;
  getCurrentOrder: MemoizedSelector<AppState, Order>;
  getCurrentSite: MemoizedSelector<AppState, Site>;
  getCurrentSubscription: MemoizedSelector<AppState, SubscriptionI>;
  getExternalLinks: MemoizedSelector<AppState, any>;
  getGroupRoles: MemoizedSelector<AppState, GroupRole[]>;
  getImpersonateUser: MemoizedSelector<AppState, any>;
  getImpersonableUsers: MemoizedSelector<AppState, any>;
  getLanguage: MemoizedSelector<AppState, any>;
  getMyClients: MemoizedSelector<AppState, Client[]>;
  getMyOrders: MemoizedSelector<AppState, Order[]>;
  getMyRoles: MemoizedSelector<AppState, Role[]>;
  getMySites: MemoizedSelector<AppState, Site[]>;
  getMySubscriptions: MemoizedSelector<AppState, SubscriptionI[]>;
  getNavigationId: MemoizedSelector<AppState, any>;
  getRouterDataBread: MemoizedSelector<AppState, string>;
  getRouterDataBreadTitle: MemoizedSelector<AppState, string>;
  getRouterBcData: MemoizedSelector<AppState, IBcData[]>;
  getRouterState: MemoizedSelector<AppState, any>;
  getSelectedItemUuid: MemoizedSelector<AppState, string>;
  getSidebarClosed: MemoizedSelector<AppState, boolean>;
  getTimezones: MemoizedSelector<AppState, Timezone[]>;
  getTimezonesByIanaCode: MemoizedSelectorWithProps<AppState, string, Timezone[]>;
  getUserInfo: MemoizedSelector<AppState, Auth0UserInfo>;
  getPreferences: MemoizedSelector<AppState, Preference[]>;
  getPreferencesByName: MemoizedSelectorWithProps<AppState, string, any>;
  getUsers: MemoizedSelector<AppState, User[]>;
  getUsersByType: MemoizedSelectorWithProps<AppState, string, User[]>;
  getProducts: MemoizedSelector<AppState, Product[]>;
  getServices: MemoizedSelector<AppState, Service[]>;
  getProductsCompleteList: MemoizedSelector<AppState, Product[]>;
  getCulturesByCode: MemoizedSelectorWithProps<AppState, string, Culture[]>;
  getServer: MemoizedSelector<AppState, ServerForm[]>;
  getServerUrl: MemoizedSelector<AppState, ServerFormUrl[]>;
  getDeviceAvailable: MemoizedSelector<AppState, Device[]>;
  getTransporters: MemoizedSelector<AppState, Transporter[]>;
  getPlayerConfigurationEdit: MemoizedSelector<AppState, PlayerConfigurationEdit[]>;
  getPlayerConfigDef: MemoizedSelector<AppState, PlayerConfigurationDefinition[]>;
  getQuickSearchFilter: MemoizedSelector<AppState, any>;
  getQuickSearchResults: MemoizedSelector<AppState, any>;
  getQuickSearchCount: MemoizedSelector<AppState, any>;
  getQuickSearchShowModal: MemoizedSelector<AppState, any>;
  getCompaniesLinkContract: MemoizedSelector<AppState, any>;
  getSubscriptionsLinkContract: MemoizedSelector<AppState, any>;
  getContractLink: MemoizedSelector<AppState, any>;
  getInvoicesFilter: MemoizedSelector<AppState, ContractFilter |  string | number>;
  getInvoicesQuickSearch: MemoizedSelector<AppState, string>;
  getInvoicesBy: MemoizedSelector<AppState, string>;
  getInvoices: MemoizedSelector<AppState, any[]>;
  getInvoicesCount: MemoizedSelector<AppState, number>;
  getInvoicesPageSize: MemoizedSelector<AppState, number>;
  getInvoicesPage: MemoizedSelector<AppState, number>;
  getInvoicesShowFilter: MemoizedSelector<AppState, boolean>;
  getHeaderTitle: MemoizedSelector<AppState, any>;

  getSubListFilter: MemoizedSelector<AppState, SubscriptionsFilter |  string | number>;
  getSubListQuickSearch: MemoizedSelector<AppState, string>;
  getSubListBy: MemoizedSelector<AppState, string>;
  getSubList: MemoizedSelector<AppState, SubscriptionsList[]>;
  getSubListCount: MemoizedSelector<AppState, number>;
  getSubListPageSize: MemoizedSelector<AppState, number>;
  getSubListPage: MemoizedSelector<AppState, number>;
  getSubListShowFilter: MemoizedSelector<AppState, boolean>;
  getSelectedSubList: MemoizedSelector<AppState, SelectedItem[]>;
  getSelectedOneSubscription: MemoizedSelector<AppState, SelectedItem>;
  getClientConfiguration: MemoizedSelector<AppState, any>;
  getCMS: MemoizedSelector<AppState, ServerForm[]>;

  getBillingProducts: MemoizedSelector<AppState, BillingProduct[]>;
  getConcepts: MemoizedSelector<AppState, Concept[]>;
  getLicences: MemoizedSelector<AppState, any[]>;


}


export const selectors: Selectors = {
  getTestData: createSelector(rootSelectors.testData, testDataSelectors.array),
  getCountries: createSelector(rootSelectors.countries, countriesSelectors.array),
  getCultures: createSelector(rootSelectors.cultures, culturesSelectors.array),
  getOffices: createSelector(rootSelectors.offices, officesSelectors.array),
  getProductionLine: createSelector(rootSelectors.productionLine, productionLineSelectors.array),
  getSupportUsers: createSelector(rootSelectors.supportUsers, supportUsersSelectors.array),
  getCurrentClient: createSelector(rootSelectors.userContext, userContextSelectors.client),
  getCurrentOrder: createSelector(rootSelectors.userContext, userContextSelectors.order),
  getCurrentSite: createSelector(rootSelectors.userContext, userContextSelectors.site),
  getCurrentSubscription: createSelector(rootSelectors.userContext, userContextSelectors.subscription),
  getExternalLinks: createSelector(rootSelectors.externalLinks, externalLinksSelectors.array),
  getGroupRoles: createSelector(rootSelectors.groupRoles, groupRolesSelectors.array),
  getImpersonateUser: createSelector(rootSelectors.userContext, userContextSelectors.impersonate),
  getImpersonableUsers: createSelector(rootSelectors.impersonableUsers, impersonableUsersSelectors.array),
  getLanguage: createSelector(rootSelectors.layout, layoutSelectors.language),
  getMyClients: createSelector(rootSelectors.myClients, myClientsSelectors.array),
  getMyOrders: createSelector(rootSelectors.myOrders, myOrdersSelectors.array),
  getMyRoles: createSelector(rootSelectors.userContext, userContextSelectors.roles),
  getMySites: createSelector(rootSelectors.mySites, mySitesSelectors.array),
  getMySubscriptions: createSelector(rootSelectors.mySubscriptions, mySubscriptionsSelectors.array),
  getNavigationId: createSelector(rootSelectors.router, (router: RouterReducerState) => _.get(router, 'navigationId')),
  getRouterDataBread: createSelector(rootSelectors.router, (router: RouterReducerState) => _.get(router, 'state.data.bread')),
  getRouterDataBreadTitle: createSelector(rootSelectors.router, (router: RouterReducerState) => _.get(router, 'state.data.tittleBread')),
  getRouterBcData: createSelector(rootSelectors.router, (router: RouterReducerState) => _.get(router, 'state.data.bcData')),
  getRouterState: createSelector(rootSelectors.router, (router: RouterReducerState) => _.get(router, 'state')),
  getSelectedItemUuid: createSelector(rootSelectors.userContext, userContextSelectors.selectedItem),
  getSidebarClosed: createSelector(rootSelectors.layout, layoutSelectors.sidebarClosed),
  getTimezones: createSelector(rootSelectors.timezones, timezonesSelectors.array),
  getTimezonesByIanaCode: createSelector(rootSelectors.timezones, (state: TimezonesState, props: string) => timezonesSelectors.getTimezonesByIanaCode(state, props)),
  getUserInfo: createSelector(rootSelectors.userInfo, userInfoSelectors.value),
  getPreferences: createSelector(rootSelectors.layout, layoutSelectors.preferences),
  getPreferencesByName: createSelector(rootSelectors.layout, (state: LayoutState, props: string) => layoutSelectors.preferencesByName(state, props)),
  getUsers: createSelector(rootSelectors.users, usersSelectors.users),
  getUsersByType: createSelector(rootSelectors.users, (state: UsersState, props: string) => usersSelectors.usersByType(state, props)),
  getProducts: createSelector(rootSelectors.products, productsSelectors.array),
  getServices: createSelector(rootSelectors.services, servicesSelectors.array),
  getProductsCompleteList: createSelector(rootSelectors.productsCompleteList, productsCompleteListSelectors.array),
  getCulturesByCode: createSelector(rootSelectors.cultures, (state: CulturesState, props: string) => culturesSelectors.cultureByCode(state, props)),
  getServer: createSelector(rootSelectors.server, serverSelectors.array),
  getServerUrl: createSelector(rootSelectors.serverUrl, serverUrlSelectors.array),
  getDeviceAvailable: createSelector(rootSelectors.deviceAvailable, deviceAvailableSelectors.array),
  getTransporters: createSelector(rootSelectors.transporters, transportersSelectors.array),
  getPlayerConfigurationEdit: createSelector(rootSelectors.playerConfigurationEdit, playerConfigurationEditSelectors.array),
  getPlayerConfigDef: createSelector(rootSelectors.playerConfigDef, playerConfigurationDefinitionSelectors.array),
  getQuickSearchFilter: createSelector(rootSelectors.quickSearch, quickSearchSelectors.filter),
  getQuickSearchResults: createSelector(rootSelectors.quickSearch, quickSearchSelectors.array),
  getQuickSearchCount: createSelector(rootSelectors.quickSearch, quickSearchSelectors.count),
  getQuickSearchShowModal: createSelector(rootSelectors.quickSearch, quickSearchSelectors.showModal),
  getCompaniesLinkContract: createSelector(rootSelectors.companiesLinkContract, companiesLinkContractSelectors.array),
  getSubscriptionsLinkContract: createSelector(rootSelectors.subscriptionsLinkContract, subscriptionsLinkContractSelectors.array),
  getContractLink: createSelector(rootSelectors.contractLink, contractLinkSelectors.array),
  getInvoicesFilter: createSelector(rootSelectors.invoicesList, invoicesListSelectors.filter),
  getInvoicesQuickSearch: createSelector(rootSelectors.invoicesList, invoicesListSelectors.quickSearch),
  getInvoicesBy: createSelector(rootSelectors.invoicesList, invoicesListSelectors.orderBy),
  getInvoices: createSelector(rootSelectors.invoicesList, invoicesListSelectors.array),
  getInvoicesCount: createSelector(rootSelectors.invoicesList, invoicesListSelectors.count),
  getInvoicesPageSize: createSelector(rootSelectors.invoicesList, invoicesListSelectors.pageSize),
  getInvoicesPage: createSelector(rootSelectors.invoicesList, invoicesListSelectors.page),
  getInvoicesShowFilter: createSelector(rootSelectors.invoicesList, invoicesListSelectors.showFilter),
  getHeaderTitle: createSelector( rootSelectors.headerTitle , headerTitleSelectors.header),

  getSubListFilter: createSelector(rootSelectors.subscriptionList, subListSelectors.filter),
  getSubListQuickSearch: createSelector(rootSelectors.subscriptionList, subListSelectors.quickSearch),
  getSubListBy: createSelector(rootSelectors.subscriptionList, subListSelectors.orderBy),
  getSubList: createSelector(rootSelectors.subscriptionList, subListSelectors.array),
  getSubListCount: createSelector(rootSelectors.subscriptionList, subListSelectors.count),
  getSubListPageSize: createSelector(rootSelectors.subscriptionList, subListSelectors.pageSize),
  getSubListPage: createSelector(rootSelectors.subscriptionList, subListSelectors.page),
  getSubListShowFilter: createSelector(rootSelectors.subscriptionList, subListSelectors.showFilter),
  getSelectedSubList: createSelector(rootSelectors.subscriptionList, subListSelectors.selectedSubscription),
  getSelectedOneSubscription: createSelector(rootSelectors.subscriptionList, subListSelectors.selectedOneSubscription),
  getClientConfiguration: createSelector(rootSelectors.clientConfiguration, clientConfigurationSelectors.array),
  getCMS: createSelector(rootSelectors.cms, cmsSelectors.array),
  getBillingProducts: createSelector(rootSelectors.billingProducts, billingProductsSelectors.array),
  getConcepts: createSelector(rootSelectors.concepts, conceptsSelectors.array),
  getLicences: createSelector(rootSelectors.licences, billingLicencesSelectors.array),
}
